import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import FilterBar from '@/pages/category/components/FilterBar';
import Pagination from '@/pages/category/components/Pagination';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  allArticles,
  getArticlesByCategory,
  getCategoryBySlug,
  parseTime,
  sortByHot,
  sortByTime,
} from '@/utils/news';

const PAGE_SIZE = 6;
const REFERENCE_TIME = Math.max(...allArticles.map((item) => parseTime(item.publishTime)));

export default function CategoryPage() {
  const { categorySlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const category = getCategoryBySlug(categorySlug);

  const [timeValue, setTimeValue] = useState('all');
  const [sortValue, setSortValue] = useState('latest');
  const [activeTag, setActiveTag] = useState(searchParams.get('tag') || '');
  const [page, setPage] = useState(1);

  usePageMeta(
    category ? `${category.name} - 热搜榜` : '分类 - 热搜榜',
    category
      ? `${category.name}类实时热点资讯：${category.description} 内容持续更新，为你呈现最新最热的${category.name}话题。`
      : '按分类浏览热搜榜的实时热点资讯。',
  );

  useEffect(() => {
    const tag = searchParams.get('tag') || '';
    setActiveTag(tag);
    setPage(1);
  }, [searchParams]);

  const baseList = useMemo(
    () => (category ? getArticlesByCategory(category.slug) : []),
    [category],
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [baseList]);

  const filtered = useMemo(() => {
    let list = baseList;
    if (timeValue !== 'all') {
      const day = 86400000;
      const span = timeValue === 'today' ? 1 : timeValue === '3days' ? 3 : 7;
      list = list.filter((item) => REFERENCE_TIME - parseTime(item.publishTime) < day * span);
    }
    if (activeTag) {
      list = list.filter((item) => item.tags.includes(activeTag));
    }
    return sortValue === 'hot' ? sortByHot(list) : sortByTime(list);
  }, [baseList, timeValue, activeTag, sortValue]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hotInCategory = sortByHot(baseList).slice(0, 5);

  const resetFilters = () => {
    setTimeValue('all');
    setSortValue('latest');
    setActiveTag('');
    setPage(1);
  };

  if (!category) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-page flex-col items-center justify-center px-4 py-24 text-center">
          <div className="flex h-16 w-16 items-center justify-center text-5xl text-foreground-300">
            <i className="ri-error-warning-line"></i>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900">未找到该分类</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在，可能链接已失效。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-page px-4 py-6 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.name }]} />
          <div className="mt-4 flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-2xl text-primary-700">
              <i className={category.icon}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {category.name}热点
              </h1>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-foreground-600">{category.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <FilterBar
          timeValue={timeValue}
          onTimeChange={(v) => {
            setTimeValue(v);
            setPage(1);
          }}
          sortValue={sortValue}
          onSortChange={(v) => {
            setSortValue(v);
            setPage(1);
          }}
          tags={tags}
          activeTag={activeTag}
          onTagChange={(v) => {
            setActiveTag(v);
            setPage(1);
          }}
          onReset={resetFilters}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center justify-between text-sm text-foreground-500">
              <span>
                共 <strong className="text-primary-600">{filtered.length}</strong> 条资讯
              </span>
              <span className="text-xs">第 {currentPage} / {totalPages} 页</span>
            </div>

            {paged.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-center">
                <div className="flex h-14 w-14 items-center justify-center text-4xl text-foreground-300">
                  <i className="ri-file-search-line"></i>
                </div>
                <p className="mt-3 text-sm text-foreground-500">暂无符合条件的资讯</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  重置筛选条件
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {paged.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}

            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                <i className="ri-fire-fill text-accent-500"></i>
                本类热门
              </h2>
              <div className="flex flex-col divide-y divide-background-200">
                {hotInCategory.map((article, index) => (
                  <ArticleCard key={article.id} article={article} variant="compact" rank={index + 1} />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}